/** @file slot.fill: puts an image in one tile slot (a loose tile-size image or the sheet image). */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import type { CommandDef } from '../apply/commandDef';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import type { Command } from '../types/command';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { fillTile } from './fillTile';
import { findSlot } from './findSlot';
import { previousFill } from './previousFill';

/** The inverse: clear an empty tile, else re-fill it with its previous image and fit. */
function inverseOf(draft: PuppetDoc, slot: string, prev: string | undefined): Command[] {
  const p = previousFill(draft, slot, prev);
  if (p.image === null) return [{ name: 'slot.clear', input: { slot } }];
  return [{ name: 'slot.fill', input: p.fit === undefined ? { slot, image: p.image } : { slot, image: p.image, fit: p.fit } }];
}

/**
 * `slot.fill { slot, image, fit? }` → `{ slot }`: an image other than the entry's sheet image must have the tile's
 * size, or `fit` (`pad` | `scale`) adapts it with `IMAGE_RESIZED` and records `images[id].fit`. Every entry accepts it,
 * a sheet-backed tile becoming a loose image. Inverse: `slot.clear`, or `slot.fill` with the previous image and fit.
 * Many slots at once: `slot.fillMany` (CR-012).
 */
export const slotFillCommand: CommandDef<'slot.fill'> = {
  name: 'slot.fill',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['slot', 'image', 'fit']);
    return compact({ slot: r.str('slot'), image: r.str('image'), fit: r.opt('fit', (k) => r.oneOf(k, ['pad', 'scale'] as const)) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const located = findSlot(draft.puppet, input.slot);
    const inverse = inverseOf(draft, input.slot, located.entry.slots[String(located.slot.tile)]);
    const warnings = fillTile(draft, ctx, located, input, '/input');
    const after = deriveSlots(draft.puppet).find((s) => s.key === input.slot) ?? located.slot;
    return { result: { slot: after }, warnings, inverse };
  },
};
